/**
 * Facilities Table V2 - Uses OmnisearchDataGridV2 for simplified, optimized rendering.
 *
 * This file demonstrates the module-level constants pattern for V2 DataGrids.
 * See hooks/omnisearch-v2/README.md for full documentation.
 */

import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { FacilitiesExportButton } from "components/FacilitiesExportButton";
import { Importer } from "components/Importer";
import { ImportDropdown } from "components/Importer/ImportDropdown";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip } from "components/StatusChip";
import { TagsDisplay } from "components/TagsDisplay";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { FacilitiesV2Query, FacilityStatus } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

type Row = FacilitiesV2Query["facilities"]["items"][number];

// ════════════════════════════════════════════════════════════════════════════
// MODULE LEVEL - Static config (stable references, zero runtime overhead)
// ════════════════════════════════════════════════════════════════════════════

const FACILITIES_QUERY = gql(`
  query FacilitiesV2($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
        streetAddress1
        city
        county
        state
        zip
        tenantId
        status
        archivedAt
        tags {
          id
          name
        }
      }
      count
    }
  }
`);

// Static config arrays/objects (no callbacks inside)
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [
  { field: "state", sort: "asc" },
  { field: "facility", sort: "asc" },
];
const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Facility Info",
    fields: ["facility", "customerFacilityId", "status", "includeArchived"],
  },
  {
    name: "Location",
    fields: ["state", "county", "city", "zip", "streetAddress1"],
  },
  { name: "Tags", fields: ["tags"] },
];
const KEY_ALIASES = { tags: "facilityTags" };
const ADDITIONAL_FILTER_KEYS = [
  { header: "Facility ID", key: "customerFacilityId" },
  { header: "City", key: "city" },
  { header: "Zip", key: "zip" },
  // Archived facilities are hidden from every default view; surfacing them is a
  // rare, deliberate act, so the control lives in the filter popover rather
  // than getting a toolbar toggle.
  { header: "Show archived", key: "includeArchived", filterKeyType: "boolean" as const },
];
const MOBILE_COLUMN_VISIBILITY = {
  status: false,
  county: false,
  streetAddress1: false,
  tags: false,
  actions: false,
};

// Static functions (no component scope dependencies)
const getItems = (data: FacilitiesV2Query) => data.facilities.items;
const getCount = (data: FacilitiesV2Query) => data.facilities.count;

// Columns have no component-scope dependencies — defined at module level for
// zero-cost stable references (avoids a useMemo([]) inside the component).
const COLUMNS: OmnisearchGridColDef<Row>[] = [
  {
    field: "facility",
    headerName: "Facility",
    flex: 0.5,
    filterKeyType: "facility",
    valueGetter: (_, row) => {
      const { name, customerFacilityId } = row;
      return `${name}${customerFacilityId ? ` (${customerFacilityId})` : ""}`;
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.15,
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(FacilityStatus).sort(),
    renderCell: ({ row }) =>
      row.archivedAt ? (
        <StatusChip color="error" label="Archived" />
      ) : (
        prettyPrintEnumValue(row.status ?? "")
      ),
  },
  {
    field: "state",
    headerName: "State",
    flex: 0.125,
  },
  {
    field: "county",
    headerName: "County",
    flex: 0.25,
  },
  {
    field: "streetAddress1",
    headerName: "Street Address",
    flex: 0.5,
    sortable: true,
    valueGetter(_, row) {
      if (!row.streetAddress1) return "";
      const { streetAddress1, city, state, zip } = row;
      return [streetAddress1, city, state, zip].filter((s) => s).join(", ");
    },
  },
  {
    field: "tags",
    headerName: "Tags",
    flex: 0.3,
    sortable: false,
    filterKeyType: "facilityTags",
    renderCell: ({ row }) => <TagsDisplay tags={row.tags} />,
  },
];

// ════════════════════════════════════════════════════════════════════════════
// COMPONENT - Only dynamic stuff here
// ════════════════════════════════════════════════════════════════════════════

export const FacilitiesTableV2 = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useCurrentUser();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [{ label: "Facilities", to: `/o/${tenantId}/facilities` }],
    [tenantId]
  );
  useBreadcrumb(breadcrumbs);

  // ──────────────────────────────────────────────────────────────────────────
  // useCallback - Command buttons (function receives merged search from grid)
  // ──────────────────────────────────────────────────────────────────────────
  // (columns are module-level COLUMNS — no useMemo needed)
  const commandButtons = useCallback(
    ({ search }: { search: string }) => {
      const buttons = [
        <FacilitiesExportButton
          key="export"
          tenantId={tenantId}
          filter={search ? { search } : undefined}
        />,
      ];
      if (isStaff) {
        buttons.unshift(
          <ImportDropdown
            key="import"
            color="staff"
            importers={[
              {
                name: "Facilities",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.Facility}
                    refetchQueries={["Facilities"]}
                  />
                ),
              },
              {
                name: "Facility Alternate IDs",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.FacilityAlternateId}
                    refetchQueries={["Facilities"]}
                  />
                ),
              },
            ]}
          />
        );
      }
      return buttons;
    },
    [isStaff, tenantId]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // useCallback - Functions that use component scope
  // ──────────────────────────────────────────────────────────────────────────
  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => {
      const search = omnisearch ? `${omnisearch} tenantId:${tenantId}` : `tenantId:${tenantId}`;
      return {
        search,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    [tenantId]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => navigate(params.row.id),
    [navigate]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <OmnisearchDataGridV2
      // Module-level constants (stable references)
      query={FACILITIES_QUERY}
      getItems={getItems}
      getCount={getCount}
      persistenceKey="chemicals-facilities-table-v2"
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      initialSortModel={INITIAL_SORT_MODEL}
      filterGroups={FILTER_GROUPS}
      keyAliases={KEY_ALIASES}
      additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
      mobileColumnVisibilityModel={MOBILE_COLUMN_VISIBILITY}
      // Memoized props (arrays with callbacks, React elements)
      columns={COLUMNS}
      commandButtons={commandButtons}
      buildVariables={buildVariables}
      onRowClick={handleRowClick}
      // Primitives (always stable)
      tenantId={tenantId}
      noRowsMessage="No facilities have been added to this organization yet."
    />
  );
};
